"use client";

import React, { useState, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { fetchWithAuth } from "@/lib/fetch-with-auth";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, Edit3, Check, Loader2, Music2, Languages, Mic2, AudioWaveform, Maximize2, Minimize2, Palette, X, ShieldAlert,
  ImagePlus,
} from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { BackgroundMediaPicker } from "@/components/media/background-media-picker";
import { useToast } from "@/hooks/use-toast";
import {
  saveLyrics, getLyricsForMedia, approveLyricsCorrection,
  type LyricsData,
} from "@/services/media-credits";
import { getWork, updateWork } from "@/services/media";
import { detectExplicit } from "@/lib/explicit-filter";
import { cn } from "@/lib/utils";
import { useMediaPlayer } from "@/contexts/media-player-context";

const SUPPORTED_LANGUAGES = [
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
  { code: 'sv', name: 'Swedish' },
  { code: 'no', name: 'Norwegian' },
  { code: 'da', name: 'Danish' },
  { code: 'fi', name: 'Finnish' },
  { code: 'nl', name: 'Dutch' },
  { code: 'pl', name: 'Polish' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'zh', name: 'Chinese (Simplified)' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hi', name: 'Hindi' },
  { code: 'tr', name: 'Turkish' },
  { code: 'id', name: 'Indonesian' },
  { code: 'en', name: 'English' },
];

// Karaoke sing-along visual templates. The highlight colour is applied
// separately (user-picked) so each template works with any accent.
type KaraokeTemplate = {
  id: string; name: string;
  container: string; active: string; idle: string; header: string; glow?: boolean;
};
// Sizes are in `em` so a single container font-size (the user's font-size
// slider) scales the whole view; templates differ by weight / transform / glow.
const KARAOKE_TEMPLATES: KaraokeTemplate[] = [
  { id: 'classic',  name: 'Classic',  container: 'bg-background',                                   active: 'text-[1.5em] font-bold',                    idle: 'text-[0.9em] text-muted-foreground/60',  header: 'text-[0.55em] font-semibold uppercase tracking-widest text-primary/70' },
  { id: 'neon',     name: 'Neon',     container: 'bg-black',                                         active: 'text-[1.7em] font-extrabold',               idle: 'text-[0.9em] text-white/40',            header: 'text-[0.55em] font-bold uppercase tracking-widest text-white/50',    glow: true },
  { id: 'bold',     name: 'Bold',     container: 'bg-background',                                     active: 'text-[1.8em] font-black uppercase tracking-wide', idle: 'text-[0.95em] uppercase text-muted-foreground/50', header: 'text-[0.55em] font-bold uppercase tracking-widest text-primary/70' },
  { id: 'minimal',  name: 'Minimal',  container: 'bg-background',                                     active: 'text-[1.3em] font-semibold',                idle: 'text-[0.85em] text-muted-foreground/50', header: 'text-[0.5em] font-medium uppercase tracking-widest text-muted-foreground/50' },
  { id: 'gradient', name: 'Gradient', container: 'bg-gradient-to-b from-secondary/20 to-background', active: 'text-[1.7em] font-extrabold',               idle: 'text-[0.9em] text-muted-foreground/50', header: 'text-[0.55em] font-semibold uppercase tracking-widest text-primary/70' },
];

interface MediaLyricsPanelProps {
  mediaId: string;
  mediaTitle: string;
  artistName: string;
  existingLyrics?: string;
  audioUrl?: string;
  existingBpm?: number | null;
  existingKey?: string | null;
}

export function MediaLyricsPanel({ mediaId, mediaTitle, artistName, existingLyrics, audioUrl, existingBpm, existingKey }: MediaLyricsPanelProps) {
  const tl = useTranslations("lyricsPanel");
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const { currentTime, duration, isActive, currentTrack } = useMediaPlayer();
  const [lyricsData, setLyricsData] = useState<LyricsData | null>(null);
  const [displayLyrics, setDisplayLyrics] = useState(existingLyrics || '');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [syncingTiming, setSyncingTiming] = useState(false);
  const [bpm, setBpm] = useState<number | null>(existingBpm ?? null);
  const [musicalKey, setMusicalKey] = useState<string | null>(existingKey ?? null);

  // Full-lyrics editor (owner/admin) — edit the whole text directly (replaces the
  // old "Correct" suggestion box, which Gordon found redundant).
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editLyricsText, setEditLyricsText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Translation state
  const [selectedLang, setSelectedLang] = useState('es');
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [translating, setTranslating] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);

  // An objective read of the lyrics (themes, perspective, language) and the
  // languages sung — from the transcription, stored on the work with the
  // saved translations (Gordon 2026-10-30: "both would be nice … english and
  // swedish translation, multi language transcribe of lyrics").
  const [analysis, setAnalysis] = useState('');
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [lyricLanguages, setLyricLanguages] = useState<string[]>([]);

  // Sing-along state
  const [singAlongMode, setSingAlongMode] = useState(false);
  const singAlongRef = useRef<HTMLDivElement>(null);
  const activeLineRef = useRef<HTMLParagraphElement>(null);

  // Karaoke customization (persisted per browser).
  const [karaokeTemplate, setKaraokeTemplate] = useState('classic');
  const [karaokeColor, setKaraokeColor] = useState('#22c55e');
  const [karaokeFullscreen, setKaraokeFullscreen] = useState(false);
  const [karaokeFontSize, setKaraokeFontSize] = useState(100); // percent scale
  const [karaokeTextColor, setKaraokeTextColor] = useState(''); // '' = theme default
  const [karaokeBgColor, setKaraokeBgColor] = useState('');     // '' = template default
  const [karaokeBgUrl, setKaraokeBgUrl] = useState('');         // image / gif / video URL
  const [bgPickerOpen, setBgPickerOpen] = useState(false);
  const [karaokeOffset, setKaraokeOffset] = useState(2);        // seconds: + ahead / − behind (range ±8)
  useEffect(() => {
    try {
      const t = localStorage.getItem('bb-karaoke-template'); if (t) setKaraokeTemplate(t);
      const c = localStorage.getItem('bb-karaoke-color'); if (c) setKaraokeColor(c);
      const fs = localStorage.getItem('bb-karaoke-fontsize'); if (fs) setKaraokeFontSize(Number(fs) || 100);
      const tc = localStorage.getItem('bb-karaoke-textcolor'); if (tc !== null) setKaraokeTextColor(tc);
      const bc = localStorage.getItem('bb-karaoke-bgcolor'); if (bc !== null) setKaraokeBgColor(bc);
      const bu = localStorage.getItem('bb-karaoke-bgurl'); if (bu !== null) setKaraokeBgUrl(bu);
      const of = localStorage.getItem('bb-karaoke-offset'); if (of !== null) setKaraokeOffset(Number(of));
    } catch { /* private mode */ }
  }, []);
  useEffect(() => { try { localStorage.setItem('bb-karaoke-template', karaokeTemplate); } catch {} }, [karaokeTemplate]);
  useEffect(() => { try { localStorage.setItem('bb-karaoke-color', karaokeColor); } catch {} }, [karaokeColor]);
  useEffect(() => { try { localStorage.setItem('bb-karaoke-fontsize', String(karaokeFontSize)); } catch {} }, [karaokeFontSize]);
  useEffect(() => { try { localStorage.setItem('bb-karaoke-textcolor', karaokeTextColor); } catch {} }, [karaokeTextColor]);
  useEffect(() => { try { localStorage.setItem('bb-karaoke-bgcolor', karaokeBgColor); } catch {} }, [karaokeBgColor]);
  useEffect(() => { try { localStorage.setItem('bb-karaoke-bgurl', karaokeBgUrl); } catch {} }, [karaokeBgUrl]);
  useEffect(() => { try { localStorage.setItem('bb-karaoke-offset', String(karaokeOffset)); } catch {} }, [karaokeOffset]);
  // Esc exits fullscreen karaoke.
  useEffect(() => {
    if (!karaokeFullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setKaraokeFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [karaokeFullscreen]);
  // Esc closes the edit panel.
  useEffect(() => {
    if (!showEditDialog) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowEditDialog(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showEditDialog]);

  const isAdmin = userProfile?.roles?.includes('admin') || userProfile?.roles?.includes('superadmin') || userProfile?.accountType === 'admin' || userProfile?.accountType === 'superadmin';
  const isCreator = userProfile?.accountType === 'creator';

  // ── Sing-along follows the PLAYER's current track ───────────────────────────
  // The panel shows the PAGE track's lyrics, but the sync clock is the GLOBAL
  // player. If the queue advances to another song, the old lyrics would keep
  // moving against the new song's timeline (Gordon: "it seems to be moving / the
  // next song's lyrics don't show"). So in sing-along we lock the karaoke to the
  // track that is actually playing: its lyrics, its title, its time.
  const [followedLyrics, setFollowedLyrics] = useState('');
  const isFollowing = singAlongMode && !!currentTrack?.id && currentTrack.id !== mediaId;
  useEffect(() => {
    if (!isFollowing || !currentTrack?.id) { setFollowedLyrics(''); return; }
    const inline = (currentTrack as any)?.lyrics;
    if (inline) { setFollowedLyrics(inline); return; }
    let cancelled = false;
    getLyricsForMedia(currentTrack.id)
      .then(d => { if (!cancelled) setFollowedLyrics(d?.lyrics || ''); })
      .catch(() => { if (!cancelled) setFollowedLyrics(''); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFollowing, currentTrack?.id]);

  const singAlongLyrics = isFollowing ? followedLyrics : displayLyrics;
  const singAlongTitle = isFollowing ? ((currentTrack as any)?.title || mediaTitle) : mediaTitle;
  const singAlongArtist = isFollowing ? ((currentTrack as any)?.artist || artistName) : artistName;
  // Only advance the highlight when the shown lyrics belong to the playing track.
  const canSync = isFollowing || currentTrack?.id === mediaId;
  const fmtTime = (s: number) => {
    if (!isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60), ss = Math.floor(s % 60);
    return `${m}:${String(ss).padStart(2, '0')}`;
  };

  // ── Parse lyrics into lines for sing-along ─────────────────────────────────
  const lyricsLines = singAlongLyrics
    ? singAlongLyrics.split('\n')
    : [];

  // Estimate active line index based on playback position. Track the FULL-array
  // index of each content line so repeated lines highlight the right occurrence.
  const contentToFull: number[] = [];
  lyricsLines.forEach((l, i) => { if (l.trim() && !l.startsWith('[')) contentToFull.push(i); });
  const contentLines = contentToFull.map(i => lyricsLines[i]);
  const totalDuration = duration || 0;
  // EXACT karaoke timing: per-line timestamps from transcription (only for the
  // page track, i.e. not while following another queued track). This replaces the
  // linear estimate that ran "a few rows ahead" (Gordon).
  const syncedLines = (!isFollowing && lyricsData?.synced && lyricsData.synced.length > 0)
    ? lyricsData.synced : null;
  const currentLineIdx = (() => {
    if (!singAlongMode || !isActive || !canSync || contentLines.length === 0) return -1;
    // ── Exact timestamped sync when we have it ──
    if (syncedLines) {
      const t = currentTime + karaokeOffset;
      let idx = -1;
      for (let i = 0; i < syncedLines.length; i++) {
        if (syncedLines[i].t <= t) idx = i; else break;
      }
      return idx < 0 ? -1 : Math.min(idx, contentLines.length - 1);
    }
    // ── Fallback: linear estimate ──
    // Guard against a bogus/zero duration (a track that errored on load reported
    // a tiny duration, which made the linear map jump straight to the OUTRO at
    // 0:07). Wait for a real duration before highlighting anything.
    if (!totalDuration || totalDuration < 1 || !isFinite(totalDuration)) return -1;
    // User-adjustable sync offset (+ ahead / − behind). Default +0.8s gives the
    // karaoke/teleprompter "slightly ahead" feel and masks the small lag after
    // start/stop; each person/track can tune it with the ⏱ slider.
    const fraction = Math.max(0, Math.min(1, (currentTime + karaokeOffset) / totalDuration));
    return Math.min(Math.floor(fraction * contentLines.length), contentLines.length - 1);
  })();
  // Full-array index of the active line (highlights the correct repeated line).
  const activeFullIdx = currentLineIdx >= 0 ? contentToFull[currentLineIdx] : -1;

  // Auto-scroll to active line (re-centres when entering/exiting fullscreen too).
  useEffect(() => {
    if (activeLineRef.current && singAlongRef.current) {
      activeLineRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentLineIdx, karaokeFullscreen]);

  // ── Translate lyrics ────────────────────────────────────────────────────────
  // Translations are saved on the work, so everyone sees them without paying
  // for a new one (only the owner/admin can write; others keep it in-session).
  const translateTo = async (lang: string, lyrics: string, saved: Record<string, string>) => {
    const langName = SUPPORTED_LANGUAGES.find(l => l.code === lang)?.name || lang;
    const resp = await fetchWithAuth('/api/ai/translate-lyrics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lyrics, targetLanguage: lang, targetLanguageName: langName, title: mediaTitle, artist: artistName }),
    });
    if (!resp.ok) throw new Error('Translation failed');
    const data = await resp.json();
    const next = { ...saved, [lang]: data.translatedLyrics as string };
    setTranslations(next);
    await updateWork(mediaId, { lyricsTranslations: next } as any).catch(() => {});
    return next;
  };

  const handleTranslate = async (lang = selectedLang) => {
    if (!displayLyrics || translating) return;
    if (lang !== selectedLang) setSelectedLang(lang);
    if (translations[lang]) {
      setShowTranslation(lang === selectedLang ? !showTranslation : true);
      return;
    }
    setTranslating(true);
    try {
      await translateTo(lang, displayLyrics, translations);
      setShowTranslation(true);
      toast({ title: `Translated to ${SUPPORTED_LANGUAGES.find(l => l.code === lang)?.name || lang}` });
    } catch {
      toast({ variant: 'destructive', title: 'Translation failed', description: 'Could not translate lyrics.' });
    } finally {
      setTranslating(false);
    }
  };

  // ── Transcribe lyrics + BPM/Key from audio ─────────────────────────────────
  // Time the CURRENT lyrics against the audio for exact karaoke sync — keeps the
  // text, just adds per-line timestamps. Removes the per-track offset guessing
  // (Gordon: "the offset is off by ~8s for this track / does it differ per tempo").
  const handleSyncTiming = async () => {
    if (!audioUrl || !displayLyrics || !user?.uid || syncingTiming) return;
    setSyncingTiming(true);
    try {
      const resp = await fetchWithAuth('/api/ai/transcribe-audio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audioUrl, title: mediaTitle, artist: artistName, lyricsToTime: displayLyrics }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Timing failed');
      const synced = Array.isArray(data.synced) ? data.synced : [];
      if (synced.length === 0) throw new Error('No timings returned — try Re-Transcribe instead.');
      await saveLyrics({
        mediaId, mediaTitle, lyrics: displayLyrics, synced,
        isAiGenerated: lyricsData?.isAiGenerated ?? false,
        aiConfidence: lyricsData?.aiConfidence ?? 1,
        status: lyricsData?.status ?? 'approved',
        submittedBy: user.uid,
        submittedByName: userProfile?.displayName || 'Editor',
      });
      setLyricsData(prev => (prev ? { ...prev, synced } as any : prev));
      toast({ title: 'Karaoke synced', description: `${synced.length} lines timed — sing-along is now exact.` });
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Sync failed', description: e?.message || 'Could not time the lyrics.' });
    } finally {
      setSyncingTiming(false);
    }
  };

  // Parental-Advisory filter: scan lyrics and flag the work explicit when it
  // contains explicit language. Runs automatically after transcribe / AI-draft /
  // save, and on demand via the "Check explicit" button. Only flips OFF→ON —
  // a creator can still untick explicit manually in the edit form.
  const applyExplicitFromLyrics = async (text: string, opts?: { silent?: boolean }) => {
    const { explicit, terms } = detectExplicit(text);
    if (explicit && mediaId) {
      await updateWork(mediaId, { explicit: true }).catch(() => {});
      try { window.dispatchEvent(new CustomEvent('bb:explicit-updated', { detail: { mediaId } })); } catch { /* ignore */ }
      if (!opts?.silent) {
        toast({ title: 'Marked Parental Advisory', description: `Explicit language detected (${terms.slice(0, 4).join(', ')}${terms.length > 4 ? '…' : ''}).` });
      }
      return true;
    }
    if (!opts?.silent) {
      toast({ title: 'No explicit language found', description: 'Lyrics look clean — the track was not flagged.' });
    }
    return false;
  };

  const handleTranscribeAudio = async () => {
    if (!audioUrl || transcribing) return;
    setTranscribing(true);
    try {
      const resp = await fetchWithAuth('/api/ai/transcribe-audio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audioUrl, title: mediaTitle, artist: artistName }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Transcription failed');

      const { lyrics: transcribedRaw, synced, bpm: detectedBpm, key: detectedKey, instrumental, lyricsUnavailable } = data;
      const newAnalysis: string = typeof data.analysis === 'string' ? data.analysis.trim() : '';
      const newLanguages: string[] = Array.isArray(data.languages) ? data.languages : [];
      if (newAnalysis || newLanguages.length) {
        if (newAnalysis) setAnalysis(newAnalysis);
        setLyricLanguages(newLanguages);
        await updateWork(mediaId, {
          ...(newAnalysis ? { lyricsAnalysis: newAnalysis } : {}),
          lyricsLanguages: newLanguages,
        } as any).catch(() => {});
      }
      // Belt-and-braces client-side guard on top of the route's own check —
      // never save the "Instrumental" sentinel (or a stray fabricated line) as
      // if it were real lyrics. Gordon: "it should not generate text for songs
      // without text."
      const transcribed = instrumental || !transcribedRaw?.trim() || /^instrumental\.?$/i.test(transcribedRaw.trim())
        ? ''
        : transcribedRaw;

      // Update BPM + Key state and save to Firestore
      if (detectedBpm) setBpm(detectedBpm);
      if (detectedKey) setMusicalKey(detectedKey);
      if ((detectedBpm || detectedKey) && mediaId) {
        await updateWork(mediaId, {
          ...(detectedBpm ? { bpm: detectedBpm } : {}),
          ...(detectedKey ? { key: detectedKey } : {}),
        }).catch(() => {}); // non-critical
      }

      // Save lyrics if we got some
      if (transcribed && user?.uid) {
        const hasSynced = Array.isArray(synced) && synced.length > 0;
        const id = await saveLyrics({
          mediaId, mediaTitle, lyrics: transcribed,
          ...(hasSynced ? { synced } : {}),
          isAiGenerated: true, aiConfidence: 0.8,
          status: 'pending_review',
          submittedBy: user.uid,
          submittedByName: userProfile?.displayName || 'AI',
        });
        setDisplayLyrics(transcribed);
        setLyricsData(prev => prev ? { ...prev, lyrics: transcribed, synced: hasSynced ? synced : prev.synced, isAiGenerated: true } : {
          id, mediaId, mediaTitle, lyrics: transcribed, synced: hasSynced ? synced : undefined,
          isAiGenerated: true, aiConfidence: 0.8, status: 'pending_review',
          submittedBy: user.uid, submittedByName: userProfile?.displayName || 'AI',
          corrections: [],
        } as any);
        // Auto-publish to the work doc too — the media player and every other
        // frontend surface read work.lyrics, not the wiki collection. The wiki
        // entry above keeps the pending_review status; lyricsAuto marks the
        // "auto-transcribed" badge until someone verifies/edits.
        await updateWork(mediaId, { lyrics: transcribed, lyricsAuto: true } as any).catch(() => {});
        toast({
          title: 'Audio Transcribed',
          description: `Lyrics extracted${hasSynced ? ' with karaoke timing' : ''}${detectedBpm ? `, BPM: ${detectedBpm}` : ''}${detectedKey ? `, Key: ${detectedKey}` : ''}`,
        });
        // Parental-Advisory filter on the freshly transcribed lyrics.
        await applyExplicitFromLyrics(transcribed, { silent: false });
        // New words → old translations are stale. Translate to English and
        // Swedish straight away unless the song is sung only in that language.
        setTranslations({});
        setShowTranslation(false);
        await updateWork(mediaId, { lyricsTranslations: {} } as any).catch(() => {});
        const targets = ['en', 'sv'].filter(code => !(newLanguages.length === 1 && newLanguages[0] === code));
        let saved: Record<string, string> = {};
        for (const code of targets) {
          try { saved = await translateTo(code, transcribed, saved); } catch { /* the Translate button still works */ }
        }
      } else if (lyricsUnavailable && newAnalysis) {
        // The model would only describe the song — keep the existing lyrics,
        // show the analysis instead.
        setShowAnalysis(true);
        toast({ title: tl("analysisOnlyTitle"), description: tl("analysisOnlyBody") });
      } else if (instrumental) {
        toast({
          title: 'No vocals detected',
          description: `The track appears to be instrumental — no lyrics were saved.${detectedBpm ? ` BPM: ${detectedBpm}` : ''}${detectedKey ? ` Key: ${detectedKey}` : ''}`,
        });
      } else if (detectedBpm || detectedKey) {
        toast({
          title: 'Analysis Complete',
          description: `${detectedBpm ? `BPM: ${detectedBpm}` : ''}${detectedBpm && detectedKey ? ' · ' : ''}${detectedKey ? `Key: ${detectedKey}` : ''}`,
        });
      } else {
        throw new Error('No lyrics or analysis data returned');
      }
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Transcription Failed', description: err.message || 'Could not transcribe audio.' });
    } finally {
      setTranscribing(false);
    }
  };

  useEffect(() => {
    const fetchLyrics = async () => {
      try {
        const [data, work] = await Promise.all([
          getLyricsForMedia(mediaId),
          getWork(mediaId).catch(() => null),
        ]);
        if (data) {
          setLyricsData(data);
          setDisplayLyrics(data.lyrics);
        } else if (existingLyrics) {
          setDisplayLyrics(existingLyrics);
        }
        const w = work as any;
        if (w?.lyricsAnalysis) setAnalysis(w.lyricsAnalysis);
        if (Array.isArray(w?.lyricsLanguages)) setLyricLanguages(w.lyricsLanguages);
        if (w?.lyricsTranslations && typeof w.lyricsTranslations === 'object') setTranslations(w.lyricsTranslations);
        // NOTE: we deliberately do NOT auto-generate lyrics here. Auto-generating
        // filled real recordings with invented/hallucinated lyrics (Gordon saw
        // wrong lyrics on real songs). Admins/creators choose explicitly:
        // "Transcribe from Audio" (real lyrics from the vocals) or "Draft with AI".
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    if (mediaId) fetchLyrics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId, existingLyrics]);

  const handleGenerateAILyrics = async () => {
    if (!user?.uid) return;
    setGenerating(true);
    try {
      // Call the AI service to generate lyrics
      const response = await fetchWithAuth('/api/ai/generate-lyrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: mediaTitle,
          artist: artistName,
          existingLyrics: displayLyrics || undefined,
        }),
      });

      if (!response.ok) {
        // Fallback: generate a placeholder
        const placeholderLyrics = `[AI-Generated Lyrics for "${mediaTitle}" by ${artistName}]\n\n[Verse 1]\n(Lyrics generation requires the AI API endpoint to be configured)\n\n[Chorus]\n(Please set up /api/ai/generate-lyrics for AI lyrics generation)\n\n[Note: These are placeholder lyrics. Configure the AI endpoint or add lyrics manually.]`;

        const id = await saveLyrics({
          mediaId,
          mediaTitle,
          lyrics: placeholderLyrics,
          isAiGenerated: true,
          aiConfidence: 0,
          status: 'draft',
          submittedBy: user.uid,
          submittedByName: userProfile?.displayName || 'AI',
        });
        setDisplayLyrics(placeholderLyrics);
        setLyricsData(prev => prev ? { ...prev, lyrics: placeholderLyrics } : {
          id, mediaId, mediaTitle, lyrics: placeholderLyrics,
          isAiGenerated: true, aiConfidence: 0, status: 'draft',
          submittedBy: user.uid, submittedByName: userProfile?.displayName || 'AI',
          corrections: [],
        } as any);
        toast({ title: "AI Lyrics", description: "Placeholder generated. Configure the AI endpoint for real lyrics." });
        return;
      }

      const data = await response.json();
      const generatedLyrics = data.lyrics || '';

      const id = await saveLyrics({
        mediaId,
        mediaTitle,
        lyrics: generatedLyrics,
        isAiGenerated: true,
        aiConfidence: data.confidence || 0.7,
        status: 'pending_review',
        submittedBy: user.uid,
        submittedByName: userProfile?.displayName || 'AI',
      });

      setDisplayLyrics(generatedLyrics);
      setLyricsData(prev => prev ? { ...prev, lyrics: generatedLyrics } : {
        id, mediaId, mediaTitle, lyrics: generatedLyrics,
        isAiGenerated: true, aiConfidence: data.confidence || 0.7, status: 'pending_review',
        submittedBy: user.uid, submittedByName: 'AI',
        corrections: [],
      } as any);
      // Auto-publish to the work doc — same as the transcription path: the
      // player and other frontend surfaces read work.lyrics.
      await updateWork(mediaId, { lyrics: generatedLyrics, lyricsAuto: true } as any).catch(() => {});
      toast({ title: "AI Lyrics Generated", description: "Lyrics have been generated and saved for review." });
      // Parental-Advisory filter on the AI-drafted lyrics.
      await applyExplicitFromLyrics(generatedLyrics, { silent: true });
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to generate lyrics." });
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!user?.uid) return;
    setSavingEdit(true);
    try {
      const cleared = !editLyricsText.trim();
      const id = await saveLyrics({
        mediaId, mediaTitle, lyrics: editLyricsText,
        isAiGenerated: false, aiConfidence: 1,
        status: 'approved',
        submittedBy: user.uid,
        submittedByName: userProfile?.displayName || 'Editor',
      });
      setDisplayLyrics(editLyricsText);
      setLyricsData(prev => prev
        ? { ...prev, id: prev.id || id, lyrics: editLyricsText, isAiGenerated: false, status: 'approved' } as any
        : { id, mediaId, mediaTitle, lyrics: editLyricsText, isAiGenerated: false, aiConfidence: 1,
            status: 'approved', submittedBy: user.uid, submittedByName: userProfile?.displayName || 'Editor', corrections: [] } as any);
      // Mirror onto the work doc so the player + SEO metadata pick up the edit.
      // A manual save (even clearing it) is no longer the auto-transcribed draft.
      // Edited words → the saved translations no longer match them.
      await updateWork(mediaId, { lyrics: editLyricsText, lyricsAuto: false, lyricsTranslations: {} } as any).catch(() => {});
      setTranslations({});
      setShowTranslation(false);
      // Parental-Advisory filter on the saved lyrics (nothing to scan if cleared).
      if (!cleared) await applyExplicitFromLyrics(editLyricsText, { silent: true });
      setShowEditDialog(false);
      toast({ title: cleared ? 'Lyrics cleared' : 'Lyrics saved', description: cleared ? undefined : 'Marked human-verified.' });
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to save lyrics.' });
    } finally {
      setSavingEdit(false);
    }
  };

  // One-click removal — same destination as saving an empty edit (both
  // persistence layers: the mediaLyrics wiki doc and the work doc's mirror),
  // just without opening the editor first. Gordon: "cant remove transcribed text".
  const handleClearLyrics = async () => {
    if (!user?.uid || !mediaId) return;
    if (!window.confirm('Remove these lyrics? This cannot be undone.')) return;
    setSavingEdit(true);
    try {
      const id = await saveLyrics({
        mediaId, mediaTitle, lyrics: '',
        isAiGenerated: false, aiConfidence: 1,
        status: 'approved',
        submittedBy: user.uid,
        submittedByName: userProfile?.displayName || 'Editor',
      });
      setDisplayLyrics('');
      setEditLyricsText('');
      setLyricsData(prev => prev ? { ...prev, id: prev.id || id, lyrics: '', synced: undefined, isAiGenerated: false, status: 'approved' } as any : prev);
      await updateWork(mediaId, { lyrics: '', lyricsAuto: false } as any).catch(() => {});
      toast({ title: 'Lyrics cleared' });
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to clear lyrics.' });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleApproveCorrection = async (correctionId: string) => {
    if (!lyricsData?.id) return;
    try {
      await approveLyricsCorrection(lyricsData.id, correctionId);
      // Refresh
      const updated = await getLyricsForMedia(mediaId);
      if (updated) {
        setLyricsData(updated);
        setDisplayLyrics(updated.lyrics);
      }
      toast({ title: "Correction Applied" });
    } catch (error) {
      toast({ variant: "destructive", title: "Error" });
    }
  };

  if (loading) {
    return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  const currentTranslation = showTranslation ? translations[selectedLang] : null;
  const langName = SUPPORTED_LANGUAGES.find(l => l.code === selectedLang)?.name || selectedLang;

  // ── Karaoke rendering (shared by the inline box and the fullscreen overlay) ──
  const tpl = KARAOKE_TEMPLATES.find(t => t.id === karaokeTemplate) || KARAOKE_TEMPLATES[0];
  const renderKaraokeLines = (big: boolean) => (
    // Base em size driven by the font-size slider; big top/bottom padding in
    // fullscreen so scrollIntoView can centre EVERY line (fixes fullscreen scroll).
    <div
      className={cn("text-center", big ? "px-4 py-[42vh]" : "p-6")}
      style={{ fontSize: `${Math.round((big ? 30 : 17) * (karaokeFontSize / 100))}px` }}
    >
      {lyricsLines.length === 0 && (
        <p className="text-muted-foreground text-base py-10">
          No lyrics for “{singAlongTitle}”.{isFollowing ? ' This track in the queue has no lyrics yet.' : ''}
        </p>
      )}
      {lyricsLines.map((line, idx) => {
        const isSectionHeader = line.startsWith('[');
        const isBlank = line.trim() === '';
        const isThisActive = !isSectionHeader && !isBlank && idx === activeFullIdx;
        return (
          <p
            key={idx}
            ref={isThisActive ? activeLineRef : undefined}
            style={
              isThisActive
                ? { color: karaokeColor, background: `${karaokeColor}22`, textShadow: tpl.glow ? `0 0 18px ${karaokeColor}` : undefined }
                : (!isSectionHeader && !isBlank && karaokeTextColor ? { color: karaokeTextColor } : undefined)
            }
            className={cn(
              "transition-all duration-200",
              isSectionHeader ? cn(tpl.header, "mt-4 mb-1 opacity-70")
                : isBlank ? "h-2"
                // Active line: full-width band with padding, scaled by template + slider.
                : isThisActive ? cn("block w-full rounded-lg px-4 py-1.5 leading-snug", tpl.active)
                : cn("leading-snug py-0.5", tpl.idle)
            )}
          >
            {line || ' '}
          </p>
        );
      })}
    </div>
  );
  // Background layer (colour / image / gif / video / YouTube) shared by
  // inline + fullscreen. A pasted YouTube URL becomes a muted, looping
  // embed scaled to COVER the stage behind the lyrics (Gordon: "added a
  // background url but can't see the youtube movie").
  const bgIsVideo = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(karaokeBgUrl);
  const bgYouTubeId =
    (karaokeBgUrl.match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?[^"'\s]*v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/) || [])[1] || '';
  const karaokeBgLayer = karaokeBgUrl ? (
    <>
      {bgYouTubeId ? (
        // 16:9 iframe sized to cover any viewport: whichever of width/height
        // is the constraint, the other overflows and gets clipped by the
        // stage's overflow-hidden. Muted + inline so autoplay is allowed;
        // playlist=<id> makes loop=1 actually loop a single video.
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${bgYouTubeId}?autoplay=1&mute=1&loop=1&playlist=${bgYouTubeId}&controls=0&modestbranding=1&playsinline=1&rel=0&iv_load_policy=3`}
          title="Karaoke background video"
          allow="autoplay; encrypted-media"
          className="absolute left-1/2 top-1/2 pointer-events-none border-0"
          style={{
            width: 'max(100%, calc(177.78 * var(--karaoke-stage-h, 100vh) / 100))',
            height: 'max(100%, 56.25vw)',
            minWidth: '177.78vh',
            minHeight: '56.25vw',
            transform: 'translate(-50%, -50%)',
          }}
        />
      ) : bgIsVideo ? (
        <video src={karaokeBgUrl} autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={karaokeBgUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
      )}
      <div className="absolute inset-0 bg-black/55" />
    </>
  ) : null;

  const karaokeToolbar = (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1 flex-wrap">
        {KARAOKE_TEMPLATES.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setKaraokeTemplate(t.id)}
            className={cn(
              "text-[11px] px-2 py-1 rounded-md border transition-colors",
              karaokeTemplate === t.id ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground"
            )}
          >
            {t.name}
          </button>
        ))}
      </div>
      {/* Font size slider */}
      <label className="flex items-center gap-1 text-[11px] text-muted-foreground" title="Font size">
        <span className="text-[9px]">A</span>
        <input
          type="range" min={60} max={220} step={5}
          value={karaokeFontSize}
          onChange={e => setKaraokeFontSize(Number(e.target.value))}
          className="w-20 cursor-pointer accent-primary"
          aria-label="Karaoke font size"
        />
        <span className="text-sm">A</span>
      </label>
      {/* Sync offset slider (− behind / + ahead) */}
      <label className="flex items-center gap-1 text-[11px] text-muted-foreground" title="Lyrics lead: + shows lyrics EARLIER (ahead of the vocal), − shows them later. Range ±8s.">
        <span className="text-[10px]">⏱</span>
        <input
          type="range" min={-8} max={8} step={0.5}
          value={karaokeOffset}
          onChange={e => setKaraokeOffset(Number(e.target.value))}
          className="w-24 cursor-pointer accent-primary"
          aria-label="Karaoke lyrics lead (seconds ahead)"
        />
        <span className="tabular-nums w-9 text-right">{karaokeOffset > 0 ? '+' : ''}{karaokeOffset.toFixed(1)}s</span>
      </label>
      {/* Highlight / text / background colours */}
      <label className="flex items-center gap-1 text-[11px] text-muted-foreground" title="Highlight colour">
        <Palette className="h-3.5 w-3.5" />
        <input type="color" value={karaokeColor} onChange={e => setKaraokeColor(e.target.value)}
          className="h-6 w-7 rounded border border-border bg-transparent cursor-pointer p-0" aria-label="Highlight colour" />
      </label>
      <label className="flex items-center gap-1 text-[11px] text-muted-foreground" title="Text colour">
        <span className="text-[9px] font-bold">T</span>
        <input type="color" value={karaokeTextColor || '#888888'} onChange={e => setKaraokeTextColor(e.target.value)}
          className="h-6 w-7 rounded border border-border bg-transparent cursor-pointer p-0" aria-label="Text colour" />
      </label>
      <label className="flex items-center gap-1 text-[11px] text-muted-foreground" title="Background colour">
        <span className="text-[9px]">BG</span>
        <input type="color" value={karaokeBgColor || '#000000'} onChange={e => setKaraokeBgColor(e.target.value)}
          className="h-6 w-7 rounded border border-border bg-transparent cursor-pointer p-0" aria-label="Background colour" />
      </label>
      {/* Background from the Media Lab — images, GIFs, mp4 */}
      <button
        type="button"
        onClick={() => setBgPickerOpen(true)}
        className="h-6 px-2 rounded border border-border bg-background text-[11px] text-foreground hover:border-primary inline-flex items-center gap-1"
        title="Pick a background image, GIF or video from your Media Lab"
      >
        <ImagePlus className="h-3 w-3" />
        {karaokeBgUrl ? 'Change bg' : 'Add bg media'}
      </button>
      {/* Kept alongside the picker: a pasted URL is still the only way to use
          a YouTube video or an asset that lives outside the Media Lab. */}
      <input
        type="text"
        value={karaokeBgUrl}
        onChange={e => setKaraokeBgUrl(e.target.value)}
        placeholder="…or paste an image / gif / video / YouTube URL"
        className="h-6 w-40 text-[11px] px-2 rounded border border-border bg-background text-foreground"
        aria-label="Background media URL"
      />
      {(karaokeBgUrl || karaokeBgColor || karaokeTextColor) && (
        <button type="button" onClick={() => { setKaraokeBgUrl(''); setKaraokeBgColor(''); setKaraokeTextColor(''); }}
          className="text-[10px] text-muted-foreground hover:text-foreground underline">reset</button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Media Lab background picker — mounted at the panel root so it works
          from both the inline stage and the fullscreen karaoke view. */}
      <BackgroundMediaPicker
        open={bgPickerOpen}
        onOpenChange={setBgPickerOpen}
        value={karaokeBgUrl}
        onPick={(a) => setKaraokeBgUrl(a.url)}
      />

      {/* Primary action buttons */}
      <div className="flex flex-wrap gap-2">
        {(isAdmin || isCreator) && audioUrl && (
          <Button size="sm" variant={displayLyrics ? "outline" : "default"} className="gap-1" onClick={handleTranscribeAudio} disabled={transcribing}
            title="Listens to the actual recording and writes down the REAL lyrics sung on the track (plus BPM + key).">
            {transcribing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <AudioWaveform className="h-3.5 w-3.5" />}
            {transcribing ? 'Transcribing…' : (displayLyrics ? 'Re-Transcribe Audio' : 'Transcribe from Audio')}
          </Button>
        )}
        {(isAdmin || isCreator) && displayLyrics && audioUrl && (
          <Button size="sm" variant={lyricsData?.synced?.length ? "outline" : "default"} className="gap-1" onClick={handleSyncTiming} disabled={syncingTiming}
            title="Times your CURRENT lyrics against the audio for EXACT karaoke sync — keeps your text, no per-track offset guessing.">
            {syncingTiming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic2 className="h-3.5 w-3.5" />}
            {syncingTiming ? 'Syncing…' : (lyricsData?.synced?.length ? 'Re-sync timing' : 'Sync timing')}
          </Button>
        )}
        {(isAdmin || isCreator) && displayLyrics && (
          <Button size="sm" variant="outline" className="gap-1"
            onClick={() => { setEditLyricsText(displayLyrics); setShowEditDialog(true); }}>
            <Edit3 className="h-3.5 w-3.5" /> Edit lyrics
          </Button>
        )}
        {(isAdmin || isCreator) && displayLyrics && (
          <Button size="sm" variant="outline" className="gap-1 text-destructive hover:text-destructive"
            onClick={handleClearLyrics} disabled={savingEdit}
            title="Remove these lyrics entirely.">
            <X className="h-3.5 w-3.5" /> Clear lyrics
          </Button>
        )}
        {(isAdmin || isCreator) && displayLyrics && (
          <Button size="sm" variant="outline" className="gap-1"
            onClick={() => applyExplicitFromLyrics(displayLyrics)}
            title="Scan these lyrics for explicit language and flag Parental Advisory if found.">
            <ShieldAlert className="h-3.5 w-3.5" /> Check explicit
          </Button>
        )}
        {(isAdmin || isCreator) && (
          <Button size="sm" variant="ghost" className="gap-1 text-muted-foreground" onClick={handleGenerateAILyrics} disabled={generating}
            title="Writes DRAFT lyrics from the title/artist — this INVENTS words and is NOT the real recording. Use “Transcribe from Audio” for the actual lyrics.">
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {generating ? 'Writing…' : (displayLyrics ? 'Re-draft (AI)' : 'Draft with AI')}
          </Button>
        )}
        {displayLyrics && (
          <Button
            size="sm"
            variant={singAlongMode ? "default" : "outline"}
            className="gap-1"
            onClick={() => setSingAlongMode(v => !v)}
          >
            <Mic2 className="h-3.5 w-3.5" />
            {singAlongMode ? 'Exit Sing-Along' : 'Sing Along'}
          </Button>
        )}
      </div>

      {/* Full lyrics editor (owner/admin). A fixed panel that stops ABOVE the media
          player bar and does NOT close on outside clicks — so you can scrub / play
          the track while editing (Gordon: "control the player without the edit
          window closing"). Close via Cancel / Save / Esc / ✕. */}
      {showEditDialog && (
        <div className="fixed left-0 right-0 top-[var(--header-height)] bottom-[56px] sm:bottom-20 z-[190] flex items-stretch justify-center bg-background/95 backdrop-blur-sm p-3 sm:p-6">
          <div className="w-full max-w-2xl flex flex-col rounded-xl border bg-background shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="min-w-0">
                <p className="text-sm font-semibold">Edit lyrics</p>
                <p className="text-xs text-muted-foreground truncate">{mediaTitle} — the player stays live below</p>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowEditDialog(false)} aria-label="Close editor"><X className="h-4 w-4" /></Button>
            </div>
            <div className="flex-1 min-h-0 p-4">
              <Textarea
                value={editLyricsText}
                onChange={e => setEditLyricsText(e.target.value)}
                className="font-mono text-sm leading-relaxed h-full min-h-[200px] resize-none"
                placeholder="Type or paste the correct lyrics…"
              />
            </div>
            <div className="flex items-center gap-2 px-4 py-3 border-t">
              <p className="text-xs text-muted-foreground hidden sm:block">
                {editLyricsText.trim() ? 'Saving marks the lyrics human-verified.' : 'Saving with an empty field removes the lyrics.'}
              </p>
              <div className="flex items-center gap-2 ml-auto">
                <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={savingEdit}>Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={savingEdit}>
                  {savingEdit ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save lyrics
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Translation row */}
      {displayLyrics && (
        <div className="flex items-center gap-2 flex-wrap">
          <Languages className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <select
            value={selectedLang}
            onChange={e => {
              setSelectedLang(e.target.value);
              setShowTranslation(false);
            }}
            className="text-xs px-2 py-1 rounded border border-border bg-background text-foreground"
          >
            {SUPPORTED_LANGUAGES.map(l => (
              <option key={l.code} value={l.code}>{l.name}</option>
            ))}
          </select>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1"
            onClick={() => handleTranslate()}
            disabled={translating}
          >
            {translating ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            {translations[selectedLang] ? (showTranslation ? 'Hide' : 'Show') : 'Translate'}
          </Button>
          {/* One tap to English / Swedish (Gordon: "english and swedish translation") */}
          {(['en', 'sv'] as const).map(code => (
            <Button
              key={code}
              size="sm"
              variant={showTranslation && selectedLang === code ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              onClick={() => handleTranslate(code)}
              disabled={translating}
              aria-pressed={showTranslation && selectedLang === code}
            >
              {code === 'en' ? 'English' : 'Svenska'}
            </Button>
          ))}
          {analysis && (
            <Button size="sm" variant={showAnalysis ? "default" : "outline"} className="h-7 text-xs gap-1" onClick={() => setShowAnalysis(v => !v)} aria-pressed={showAnalysis}>
              <Sparkles className="h-3 w-3" /> {tl("analysis")}
            </Button>
          )}
          {showTranslation && translations[selectedLang] && (
            <span className="text-[10px] text-muted-foreground">
              Translated to {langName}
            </span>
          )}
        </div>
      )}

      {/* Analysis of the lyrics, from the transcription */}
      {analysis && (showAnalysis || !displayLyrics) && (
        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> {tl("analysisTitle")}
            {lyricLanguages.length > 0 && <span className="normal-case font-normal">· {lyricLanguages.map(c => c.toUpperCase()).join(" / ")}</span>}
          </p>
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{analysis}</p>
          <p className="text-[10px] text-muted-foreground">{tl("analysisNote")}</p>
        </div>
      )}

      {/* BPM + Key badges */}
      {(bpm || musicalKey) && (
        <div className="flex items-center gap-2 flex-wrap">
          {bpm && (
            <Badge variant="secondary" className="text-xs gap-1 font-mono">
              <AudioWaveform className="h-3 w-3" /> {bpm} BPM
            </Badge>
          )}
          {musicalKey && (
            <Badge variant="secondary" className="text-xs gap-1">
              <Music2 className="h-3 w-3" /> {musicalKey}
            </Badge>
          )}
        </div>
      )}

      {/* AI badge */}
      {lyricsData?.isAiGenerated && (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs gap-1">
            <Sparkles className="h-3 w-3" /> AI Generated
          </Badge>
          {lyricsData.status && (
            <Badge variant="outline" className={cn("text-xs",
              lyricsData.status === 'approved' ? 'text-green-600' :
              lyricsData.status === 'pending_review' ? 'text-yellow-600' :
              lyricsData.status === 'draft' ? 'text-muted-foreground' : ''
            )}>
              {lyricsData.status.replace('_', ' ')}
            </Badge>
          )}
        </div>
      )}

      {/* ── Sing-Along Mode ── */}
      {singAlongMode && displayLyrics ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {karaokeToolbar}
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs ml-auto" onClick={() => setKaraokeFullscreen(true)}>
              <Maximize2 className="h-3.5 w-3.5" /> Fullscreen
            </Button>
          </div>
          {!karaokeFullscreen && (
            <div
              className={cn("relative rounded-xl border border-border/60 overflow-hidden", !karaokeBgColor && !karaokeBgUrl && tpl.container)}
              style={{ background: karaokeBgColor || undefined }}
            >
              {karaokeBgLayer}
              <div ref={singAlongRef} className="relative z-10 overflow-y-auto" style={{ maxHeight: 400 }}>
                {renderKaraokeLines(false)}
                {!isActive && (
                  <div className="text-center py-3 text-xs text-muted-foreground/60 border-t border-border/40">
                    Start playing the track for synchronized lyrics
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : displayLyrics ? (
        /* ── Standard lyrics display ── */
        <div className="space-y-4">
          <div className="bg-secondary/30 rounded-lg p-4">
            <p className="text-sm leading-relaxed whitespace-pre-line">{displayLyrics}</p>
          </div>
          {/* Translation panel */}
          {currentTranslation && (
            <div className="bg-secondary/20 rounded-lg p-4 border border-border/50">
              <p className="text-[10px] font-semibold text-primary/70 uppercase tracking-wider mb-2">
                {langName} Translation
              </p>
              <p className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">
                {currentTranslation}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 space-y-3">
          <Music2 className="h-10 w-10 mx-auto text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">No lyrics available yet</p>
          <p className="text-xs text-muted-foreground/60">
            {(isAdmin || isCreator)
              ? (audioUrl ? 'Use "Transcribe from Audio" for the real lyrics, or draft with AI'
                          : 'Draft with AI or add lyrics manually')
              : 'Lyrics coming soon'}
          </p>
        </div>
      )}

      {/* Pending corrections */}
      {lyricsData?.corrections && lyricsData.corrections.filter(c => c.status === 'pending').length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground">Pending Corrections</h4>
          {lyricsData.corrections.filter(c => c.status === 'pending').map(correction => (
            <div key={correction.id} className="p-3 rounded-lg border bg-card space-y-2">
              <p className="text-xs text-muted-foreground">By {correction.userName}</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-red-500/5 p-2 rounded border border-red-500/10">
                  <p className="text-[10px] text-red-500 font-medium mb-1">Original</p>
                  <p className="line-through text-muted-foreground">{correction.originalText}</p>
                </div>
                <div className="bg-green-500/5 p-2 rounded border border-green-500/10">
                  <p className="text-[10px] text-green-500 font-medium mb-1">Corrected</p>
                  <p>{correction.correctedText}</p>
                </div>
              </div>
              {(isAdmin || lyricsData.submittedBy === user?.uid) && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="text-green-600 h-7 text-xs" onClick={() => handleApproveCorrection(correction.id)}>
                    <Check className="h-3 w-3 mr-1" /> Approve
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Fullscreen karaoke overlay ── */}
      {karaokeFullscreen && singAlongMode && displayLyrics && (
        <div
          // Stop ABOVE the media player bar (56px mobile / 80px desktop) so playback
          // controls stay reachable during fullscreen karaoke (Gordon: "need to
          // access the media player in fullscreen sing-along"). This also leaves the
          // chat FAB (bottom-0) uncovered.
          className={cn("fixed top-0 left-0 right-0 bottom-[56px] sm:bottom-20 z-[200] flex flex-col overflow-hidden", !karaokeBgColor && !karaokeBgUrl && "bg-background/98 backdrop-blur-md", (!karaokeBgColor && !karaokeBgUrl) && tpl.container)}
          style={{ background: karaokeBgColor || undefined }}
        >
          {karaokeBgLayer}
          <div className="relative z-10 flex items-center justify-between gap-2 p-3 border-b border-border/40 flex-wrap bg-background/40 backdrop-blur-sm">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{singAlongTitle}</p>
              <p className="text-xs text-muted-foreground truncate">
                {singAlongArtist}
                <span className="ml-2 font-mono tabular-nums text-foreground/70">{fmtTime(currentTime)} / {fmtTime(totalDuration)}</span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {karaokeToolbar}
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => setKaraokeFullscreen(false)}>
                <Minimize2 className="h-3.5 w-3.5" /> Exit
              </Button>
            </div>
          </div>
          {/* No justify-center here — it broke the scroll. renderKaraokeLines adds
              py-[42vh] so the active line always centres via scrollIntoView. */}
          <div ref={singAlongRef} className="relative z-10 flex-1 overflow-y-auto">
            {renderKaraokeLines(true)}
          </div>
          {!isActive && (
            <div className="relative z-10 text-center py-3 text-xs text-muted-foreground/70 border-t border-border/40 bg-background/40">
              Start playing the track for synchronized lyrics · Esc to exit
            </div>
          )}
        </div>
      )}
    </div>
  );
}
